import { GET_ENTRIES } from "../apollo/queries"
import client from "../apollo/client"
import log from "../utils/log"
import { safeReadQuery } from "../utils/user-data"
import { setApolloState } from "../refactored-functions/state"
import { CURRENT_ENTRY_DATA } from "../apollo/data"

const logEvent = (type, message, setValue, logValue = "") => {
  log(type, [message, logValue])
  return setValue
}

/**
 * Validates the response from network has expected
 * data.
 * @param {Object} entries response from network
 */
export const verifyEntriesFromNetwork = async entries =>
  entries.data && entries.data.userEntries
    ? entries.data.userEntries
    : logEvent(
        "log",
        "Entries: verifyEntriesFromNetwork `entries` param has invalid data",
        [],
        entries
      )

/**
 * Verifies the userEntries key is present in the
 * cache before attempting to access it.
 */
export const verifyEntriesInCache = () => {
  try {
    client.query({
      query: GET_ENTRIES,
    })
    return true
  } catch (error) {
    return logEvent(
      "log",
      "Entries: verifyEntriesInCache GET_ENTRIES query failed",
      false,
      error
    )
  }
}

/**
 * Retrieves all user entries from the cache, if present,
 * and if not from network.  Optionally, may force results
 * from network.
 * @param {Boolean} network force network results
 */
export const getAllEntries = (network = false) =>
  !network ? getAllEntriesFromCache() : getAllEntriesFromNetwork()

export const getAllEntriesFromNetwork = async () =>
  verifyEntriesFromNetwork(
    await client.query({
      query: GET_ENTRIES,
    })
  )

/**
 * Verifies userEntries key is in cache and if
 * so returns the user's entries array from cache.
 */
export const getAllEntriesFromCache = () =>
  verifyEntriesInCache()
    ? safeReadQuery(GET_ENTRIES, { userEntries: [] }).userEntries
    : []

/**
 * Filters the user's entries by the given type.
 * @param {Object} entries array of user entries
 */
export const getEntriesByType = entries => type =>
  entries && Array.isArray(entries)
    ? entries.filter(x => x.type === type)
    : logEvent(
        "warn",
        "Entries: getEntriesByType `entries` param has invalid data",
        [],
        entries
      )

export const getEntriesByYear = entries => year =>
  entries && Array.isArray(entries) && !!year
    ? entries.filter(
        x =>
          x.year === `${year}` &&
          x.type !== "monthly review" &&
          x.type !== "yearly review"
      )
    : logEvent(
        "warn",
        "Entries: getEntriesByDate `entries` param has invalid data",
        [],
        { entries, year }
      )

export const getEntriesByMonthYear = entries => (month, year) =>
  entries && Array.isArray(entries) && !!month && !!year
    ? entries.filter(
        x =>
          x.month === month &&
          x.year === `${year}` &&
          x.type !== "monthly review" &&
          x.type !== "yearly review"
      )
    : logEvent(
        "warn",
        "Entries: getEntriesByDate `entries` param has invalid data",
        [],
        { entries, month, year }
      )

/**
 * Filters the user's entries by month and year.
 * @param {Object} entries array of user entries
 */
export const getEntriesByDate = entries => (month, year) =>
  !!month && !!year
    ? getEntriesByMonthYear(entries)(month, year)
    : getEntriesByYear(entries)((year = month))
/**
 * Calculates the number of lowlights for the given
 * month and year.
 * @param {String} month the month
 * @param {String} year the year
 */
export const getNumberOfLowlights = (month, year) =>
  getEntriesByType(getEntriesByDate(getAllEntries())(month, year))("lowlight")
    .length

/**
 * how many pinned entries are there in a list
 */
export const getNumberOfPinned = entries =>
  entries.filter(x => x.pinned === true).length

export const getMonthlyReviewByDate = (month, year) => {
  let review
  const monthlyReviews = getEntriesByType(getAllEntries())("monthly review")
  const filtered = monthlyReviews.filter(
    x => x.month === month && x.year === year
  )
  try {
    review = filtered.length > 0 ? JSON.parse(filtered[0].review) : null
  } catch (e) {
    review = null
  }
  console.log("TCL: getMonthlyReviewByDate -> monthlyReviews", {
    month,
    year,
    monthlyReviews,
    filtered,
    review,
  })
  return review
}

export const resetCurrentEntryData = () => {
  setApolloState("currentEntry", CURRENT_ENTRY_DATA)
}

/**
 * DEBUG INFORMATION
 */
const debug = () => {
  window.debug_entries = {}
  window.debug_entries.veic = verifyEntriesInCache
  window.debug_entries.gaefn = getAllEntriesFromNetwork
  window.debug_entries.gaefc = getAllEntriesFromCache
  window.debug_entries.gae = getAllEntries
  window.debug_entries.gebd = getEntriesByDate
  window.debug_entries.gebt = getEntriesByType

  window.debug_entries.getEntriesFromMonth = (month, year) =>
    getEntriesByDate(getAllEntries())(month, year)

  window.debug_entries.getAllHighlights = (type = "highlight") =>
    getEntriesByType(getAllEntries())(type)

  window.debug_entries.getAllHighlightsFromAug2019 = (month, year, type) =>
    getEntriesByType(getEntriesByDate(getAllEntries())(month, year))(type)

  window.debug_entries.gmrbd = getMonthlyReviewByDate
}
// debug()
